/**
 * Soft floating balls behind the home/menu UI.
 * Battery: pause while game-active or document.hidden; ~24fps in menus;
 * static paint only under prefers-reduced-motion.
 */

type HomeBall = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
    alpha: number;
};

const BALL_COUNT = 14;
const BALL_RADIUS = 12;
const MAX_SPEED = 0.42;
/** Cap menu ambient below display refresh (ProMotion) without touching gameplay FPS. */
const MENU_AMBIENT_MIN_FRAME_MS = 1000 / 24;

function readBallColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue("--ball-color").trim() || "#66ffff";
}

function readBallColorTo(): string | null {
    if (
        !document.body.classList.contains("admin-theme-balls-gradient") &&
        !document.body.classList.contains("player-theme-balls-gradient")
    ) {
        return null;
    }
    const v = getComputedStyle(document.documentElement).getPropertyValue("--ball-color-to").trim();
    return v || null;
}

function parseHex(hex: string): [number, number, number] {
    const h = hex.replace("#", "");
    if (h.length !== 6) return [102, 255, 255];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function mixRgb(
    a: [number, number, number],
    b: [number, number, number],
    t: number,
): [number, number, number] {
    return [
        Math.round(a[0] + (b[0] - a[0]) * t),
        Math.round(a[1] + (b[1] - a[1]) * t),
        Math.round(a[2] + (b[2] - a[2]) * t),
    ];
}

let ambientEnabled = true;
let ambientResyncFn: (() => void) | null = null;

function menuAmbientShouldRun(): boolean {
    if (!ambientEnabled) return false;
    if (typeof document === "undefined") return false;
    if (document.hidden) return false;
    if (document.body.classList.contains("account-bootstrap-active")) return false;
    if (document.body.classList.contains("game-active")) return false;
    return true;
}

/** Sync menu ambient balls with Settings (Accessibility). */
export function syncHomeAmbientBallsEnabled(on: boolean): void {
    ambientEnabled = !!on;
    ambientResyncFn?.();
}

function drawSoftBall(
    ctx: CanvasRenderingContext2D,
    b: HomeBall,
    colorRgb: [number, number, number],
    colorToRgb: [number, number, number] | null,
): void {
    const [cr, cg, cb] = colorRgb;
    const [tr, tg, tb] = colorToRgb || colorRgb;
    const mid = mixRgb(colorRgb, [tr, tg, tb], 0.45);
    const glowR = b.r * 2.65;
    if (colorToRgb) {
        const grad = ctx.createLinearGradient(b.x - glowR, b.y - glowR, b.x + glowR, b.y + glowR);
        grad.addColorStop(0, `rgba(${tr},${tg},${tb},${b.alpha * 0.92})`);
        grad.addColorStop(0.5, `rgba(${mid[0]},${mid[1]},${mid[2]},${b.alpha * 0.45})`);
        grad.addColorStop(1, `rgba(${cr},${cg},${cb},${b.alpha * 0.12})`);
        ctx.fillStyle = grad;
    } else {
        const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, glowR);
        grad.addColorStop(0, `rgba(${cr},${cg},${cb},${b.alpha * 0.95})`);
        grad.addColorStop(0.35, `rgba(${cr},${cg},${cb},${b.alpha * 0.42})`);
        grad.addColorStop(0.72, `rgba(${cr},${cg},${cb},${b.alpha * 0.12})`);
        grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
        ctx.fillStyle = grad;
    }
    ctx.beginPath();
    ctx.arc(b.x, b.y, glowR, 0, Math.PI * 2);
    ctx.fill();
}

export function mountHomeAmbientBalls(): void {
    const canvas = document.getElementById("homeAmbientBalls") as HTMLCanvasElement | null;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reducedMotion = reducedMotionQuery.matches;
    let balls: HomeBall[] = [];
    let rafId = 0;
    let running = false;
    let lastFrameMs = performance.now();
    let lastLimitedFrameAt = 0;
    const REFERENCE_HZ = 60;
    let colorRgb = parseHex(readBallColor());
    let colorToRgb: [number, number, number] | null = null;

    function refreshBallColors(): void {
        colorRgb = parseHex(readBallColor());
        const to = readBallColorTo();
        colorToRgb = to ? parseHex(to) : null;
    }

    function resize(): void {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = window.innerWidth;
        const h = window.innerHeight;
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        if (!balls.length) seedBalls(w, h);
        else clampBalls(w, h);
    }

    function seedBalls(w: number, h: number): void {
        balls = [];
        for (let i = 0; i < BALL_COUNT; i++) {
            const r = BALL_RADIUS;
            balls.push({
                x: r + Math.random() * (w - r * 2),
                y: r + Math.random() * (h - r * 2),
                vx: (Math.random() * 2 - 1) * MAX_SPEED,
                vy: (Math.random() * 2 - 1) * MAX_SPEED,
                r,
                alpha: 0.12 + Math.random() * 0.1,
            });
        }
    }

    function clampBalls(w: number, h: number): void {
        for (const b of balls) {
            b.x = Math.min(w - b.r, Math.max(b.r, b.x));
            b.y = Math.min(h - b.r, Math.max(b.r, b.y));
        }
    }

    function paintStatic(): void {
        const w = window.innerWidth;
        const h = window.innerHeight;
        refreshBallColors();
        ctx.clearRect(0, 0, w, h);
        for (const b of balls) drawSoftBall(ctx, b, colorRgb, colorToRgb);
    }

    function step(w: number, h: number, dt: number): void {
        const fs = dt * REFERENCE_HZ;
        for (const b of balls) {
            b.x += b.vx * fs;
            b.y += b.vy * fs;
            if (b.x < b.r) {
                b.x = b.r;
                b.vx *= -1;
            } else if (b.x > w - b.r) {
                b.x = w - b.r;
                b.vx *= -1;
            }
            if (b.y < b.r) {
                b.y = b.r;
                b.vy *= -1;
            } else if (b.y > h - b.r) {
                b.y = h - b.r;
                b.vy *= -1;
            }
            drawSoftBall(ctx, b, colorRgb, colorToRgb);
        }
    }

    function draw(now: number): void {
        if (!running) return;
        if (!menuAmbientShouldRun()) {
            stop();
            return;
        }
        if (now - lastLimitedFrameAt < MENU_AMBIENT_MIN_FRAME_MS) {
            rafId = requestAnimationFrame(draw);
            return;
        }
        lastLimitedFrameAt = now;
        const dt = Math.min(0.05, (now - lastFrameMs) / 1000);
        lastFrameMs = now;
        const w = window.innerWidth;
        const h = window.innerHeight;
        refreshBallColors();
        ctx.clearRect(0, 0, w, h);
        step(w, h, dt);
        rafId = requestAnimationFrame(draw);
    }

    function stop(): void {
        running = false;
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = 0;
        }
    }

    function hideCanvas(): void {
        stop();
        canvas.classList.add("hidden");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    function start(): void {
        if (!menuAmbientShouldRun()) {
            hideCanvas();
            return;
        }
        canvas.classList.remove("hidden");
        resize();
        if (reducedMotion) {
            stop();
            paintStatic();
            return;
        }
        if (running) return;
        running = true;
        lastFrameMs = performance.now();
        lastLimitedFrameAt = 0;
        rafId = requestAnimationFrame(draw);
    }

    function onVisibility(): void {
        reducedMotion = reducedMotionQuery.matches;
        if (!menuAmbientShouldRun()) hideCanvas();
        else start();
    }

    ambientResyncFn = onVisibility;

    const bodyObserver = new MutationObserver(onVisibility);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    document.addEventListener("visibilitychange", onVisibility);
    if (typeof reducedMotionQuery.addEventListener === "function") {
        reducedMotionQuery.addEventListener("change", onVisibility);
    } else {
        reducedMotionQuery.addListener(onVisibility);
    }

    window.addEventListener("resize", () => {
        if (!menuAmbientShouldRun()) return;
        resize();
        if (reducedMotion) paintStatic();
        else if (running) {
            /* next draw uses new size */
        }
    });

    onVisibility();
}
